import { useTranslation } from 'react-i18next';

import { useNotification } from './useNotification';
import { isApiError } from '@/api/REST/errors';
import { formatErrorsForDisplay } from '@/api/REST/errors/errorDisplayUtils';

/**
 * Global error handler hook
 * Catches unhandled React Query errors and displays them as toasts
 * Should be used at app root level
 *
 * @returns Object with handleGlobalError function
 */
export const useGlobalErrorHandler = () => {
  const { showError } = useNotification();
  const { t, i18n } = useTranslation('errors');

  const handleGlobalError = (error: unknown) => {
    if (isApiError(error)) {
      const errorDisplay = formatErrorsForDisplay(error, {
        t,
        i18n,
        namespace: 'errors',
      });

      // For multiple errors, show them as a list
      if (Array.isArray(errorDisplay)) {
        showError({
          message: (
            <>
              {errorDisplay.map((msg, idx) => (
                <div key={idx}>• {msg}</div>
              ))}
            </>
          ),
          persist: true, // Multiple errors should persist
        });
      } else {
        showError({
          message: errorDisplay,
        });
      }
    } else {
      const message =
        error instanceof Error
          ? error.message
          : typeof error === 'object' &&
              error !== null &&
              'message' in error &&
              typeof (error as Record<string, unknown>).message === 'string'
            ? ((error as Record<string, unknown>).message as string)
            : undefined;
      showError({
        message: message || t('common.UnexpectedError', { ns: 'errors' }),
      });
    }
  };

  return { handleGlobalError };
};
